import React, { useState, useEffect, useMemo } from 'react';
import {
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Factura } from './types';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/useAuthStore';
import { usePaymentMethodsStore } from '../../stores/usePaymentMethodsStore';
import { formatPaymentMethods, formatPaymentMethodsEmail } from '../../utils/paymentMethods';
import { formatPhoneNumberForWhatsApp, getCountryDialCode } from '../../utils/whatsapp';
import { numeroFacturaParaMostrar } from '../../utils/facturaNumeroCanal';
import {
  buildFacturaPdfBlob,
  downloadFacturaPdfBlob,
  openFacturaPdfBlobInNewTab,
  safeFacturaPdfFilename,
  shareFacturaPdfBlob,
  canSharePdfFiles,
} from '../../utils/facturaPdfBlob';
import { uploadFacturaPdfPublicLink } from '../../utils/facturaPdfShareUpload';
import { Share2 } from 'lucide-react';

interface EnviarFacturaProps {
  factura: Factura;
  onClose: () => void;
  onEnviar: (canal: 'WHATSAPP' | 'EMAIL' | 'INSTAGRAM') => void;
}

export const EnviarFactura: React.FC<EnviarFacturaProps> = ({ factura, onClose, onEnviar }) => {
  const { usuario, currentEmpresa } = useAuthStore();
  const { paymentMethods: _paymentMethods, fetchPaymentMethods, getActivePaymentMethods } = usePaymentMethodsStore();
  const [canalSeleccionado, setCanalSeleccionado] = useState<'WHATSAPP' | 'EMAIL' | 'INSTAGRAM'>('WHATSAPP');
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([]);

  const numeroMostrar = numeroFacturaParaMostrar(factura.numero, factura.canalVenta);

  // Mensajes predeterminados para cada canal
  const mensajesPredeterminados = {
    WHATSAPP: `Hola ${factura.cliente?.nombre || 'Cliente'}, te envío la factura ${numeroMostrar} por un total de $${factura.total.toLocaleString('es-CO')}. Por favor revisa los detalles y confirma si está todo correcto.`,
    EMAIL: `Estimado/a ${factura.cliente?.nombre || 'Cliente'},\n\nAdjunto la factura ${numeroMostrar} por un total de $${factura.total.toLocaleString('es-CO')}.\n\nPor favor revisa los detalles y confirma si está todo correcto.\n\nSaludos cordiales.`,
    INSTAGRAM: `Hola ${factura.cliente?.nombre || 'Cliente'}! 📋 Te envío la factura ${numeroMostrar} por $${factura.total.toLocaleString('es-CO')}. Revisa los detalles y confirma si está todo bien 👍`
  };
  
  const [mensajePersonalizado, setMensajePersonalizado] = useState(mensajesPredeterminados.WHATSAPP);
  const [enviando, setEnviando] = useState(false);
  const [mensajeEnviado, setMensajeEnviado] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const shareAvailable = useMemo(() => canSharePdfFiles(), []);

  const copiarMensaje = async () => {
    const msg = mensajePersonalizado.trim();
    if (!msg) return;
    try {
      await navigator.clipboard.writeText(msg);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      alert('No se pudo copiar. Selecciona el texto y cópialo manualmente.');
    }
  };

  const defaultCountry = getCountryDialCode((factura.cliente as any)?.pais || (currentEmpresa as any)?.pais);
  const hasEmail = !!factura.cliente?.email?.trim();
  const hasPhone = !!formatPhoneNumberForWhatsApp(factura.cliente?.telefono || '', defaultCountry);
  const canSendByChannel =
    canalSeleccionado === 'WHATSAPP' ? hasPhone
    : canalSeleccionado === 'EMAIL' ? hasEmail
    : true;
  const sendDisabled = enviando || !mensajePersonalizado.trim() || !canSendByChannel;

  // Cargar métodos de pago al abrir el modal
  useEffect(() => {
    if (currentEmpresa?.id) {
      fetchPaymentMethods(currentEmpresa.id);
    }
  }, [currentEmpresa?.id, fetchPaymentMethods]);

  // Obtener métodos de pago activos que se muestran en facturas
  const availablePaymentMethods = getActivePaymentMethods(currentEmpresa?.id || '').filter(
    pm => pm.show_on_invoices
  );

  const buildPdfCompanyInfo = () => {
    const companyName = currentEmpresa?.nombre || 'Mi Empresa';
    const companyLogo =
      (currentEmpresa as any)?.logo || (currentEmpresa as any)?.configuracion?.branding?.logo || null;
    const companyEmail = (currentEmpresa as any)?.email || '';
    const companyPhone = (currentEmpresa as any)?.telefono || '';
    const companyAddress = (currentEmpresa as any)?.direccion || '';
    const paymentMethodsForPdf = getActivePaymentMethods(currentEmpresa?.id || '').filter(
      (pm) => pm.show_on_invoices,
    );
    return {
      companyName,
      companyLogo,
      companyEmail,
      companyPhone,
      companyAddress,
      paymentMethods: paymentMethodsForPdf,
    };
  };

  const descargarPDF = async () => {
    setPdfBusy(true);
    try {
      if (currentEmpresa?.id) await fetchPaymentMethods(currentEmpresa.id);
      const blob = await buildFacturaPdfBlob(factura, buildPdfCompanyInfo());
      downloadFacturaPdfBlob(blob, safeFacturaPdfFilename(numeroMostrar));
    } catch (e) {
      console.error('descargarPDF', e);
      alert('No se pudo generar el PDF. Intenta de nuevo o usa otro navegador.');
    } finally {
      setPdfBusy(false);
    }
  };

  const compartirPDF = async () => {
    if (!shareAvailable) {
      alert(
        'Compartir PDF no está disponible en este navegador.\n\n' +
          'Usa Safari en Mac o iPhone, o el botón «Descargar PDF».',
      );
      return;
    }
    setPdfBusy(true);
    try {
      if (currentEmpresa?.id) await fetchPaymentMethods(currentEmpresa.id);
      const blob = await buildFacturaPdfBlob(factura, buildPdfCompanyInfo());
      const pdfName = safeFacturaPdfFilename(numeroMostrar);
      const ok = await shareFacturaPdfBlob(blob, pdfName, {
        title: `Factura ${numeroMostrar}`,
        text: `Factura ${numeroMostrar} — ${currentEmpresa?.nombre || 'Clientelo'}`,
      });
      if (!ok) {
        alert('No se pudo abrir Compartir. Prueba «Descargar PDF».');
      }
    } catch (e: unknown) {
      const name = e && typeof e === 'object' && 'name' in e ? String((e as { name: string }).name) : '';
      if (name === 'AbortError') {
        return;
      }
      console.error('compartirPDF', e);
      alert('No se pudo generar el PDF.');
    } finally {
      setPdfBusy(false);
    }
  };

  const obtenerIconoCanal = (canal: string) => {
    switch (canal) {
      case 'WHATSAPP': return <ChatBubbleLeftRightIcon className="w-5 h-5" />;
      case 'EMAIL': return <EnvelopeIcon className="w-5 h-5" />;
      case 'INSTAGRAM': return <PhotoIcon className="w-5 h-5" />;
      default: return <ChatBubbleLeftRightIcon className="w-5 h-5" />;
    }
  };

  const obtenerColorCanal = (canal: string) => {
    switch (canal) {
      case 'WHATSAPP': return 'bg-green-100 text-green-800 border-green-200';
      case 'EMAIL': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'INSTAGRAM': return 'bg-pink-100 text-pink-800 border-pink-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const obtenerColorCanalHover = (canal: string) => {
    switch (canal) {
      case 'WHATSAPP': return 'hover:bg-green-200';
      case 'EMAIL': return 'hover:bg-blue-200';
      case 'INSTAGRAM': return 'hover:bg-pink-200';
      default: return 'hover:bg-gray-200';
    }
  };

  const enviarFactura = async () => {
    let finalMessage = mensajePersonalizado.trim();
    if (!finalMessage) {
      alert('Por favor ingresa un mensaje');
      return;
    }

    if (!currentEmpresa?.id || !usuario?.id) {
      alert('Error: No se encontró la empresa o usuario');
      return;
    }

    // Agregar métodos de pago seleccionados al mensaje
    if (selectedPaymentMethods.length > 0) {
      const selectedMethods = availablePaymentMethods.filter(
        pm => selectedPaymentMethods.includes(pm.id)
      );
      if (canalSeleccionado === 'EMAIL') {
        finalMessage += formatPaymentMethodsEmail(selectedMethods);
      } else {
        finalMessage += formatPaymentMethods(selectedMethods);
      }
    }

    const formattedPhone = formatPhoneNumberForWhatsApp(factura.cliente?.telefono || '', defaultCountry);

    if (canalSeleccionado === 'WHATSAPP') {
      if (!formattedPhone) {
        alert('El cliente no tiene un número de teléfono registrado');
        return;
      }
    }

    if (canalSeleccionado === 'EMAIL') {
      if (!factura.cliente?.email?.trim()) {
        alert('El cliente no tiene un email registrado');
        return;
      }
    }

    setEnviando(true);

    try {
      if (currentEmpresa?.id) await fetchPaymentMethods(currentEmpresa.id);
      const pdfBlob = await buildFacturaPdfBlob(factura, buildPdfCompanyInfo());
      const pdfName = safeFacturaPdfFilename(numeroMostrar);

      let publicPdfUrl: string | null = null;
      if (currentEmpresa?.id && factura.id) {
        publicPdfUrl = await uploadFacturaPdfPublicLink(
          pdfBlob,
          currentEmpresa.id,
          factura.id,
          pdfName.replace(/\.pdf$/i, ''),
        );
      }

      const messageForWhatsApp =
        publicPdfUrl != null
          ? `${finalMessage}\n\n📄 Factura PDF: ${publicPdfUrl}`
          : finalMessage;

      let channelOpened = false;

      // 1. Intentar compartir el PDF real (móvil / Chrome con Web Share API); si no, descargar + abrir canal
      switch (canalSeleccionado) {
        case 'WHATSAPP': {
          if (!formattedPhone) {
            alert('El cliente no tiene un número de teléfono registrado');
            setEnviando(false);
            return;
          }
          try {
            const shared = await shareFacturaPdfBlob(pdfBlob, pdfName, {
              text: messageForWhatsApp,
              title: `Factura ${numeroMostrar}`,
            });
            if (shared) {
              channelOpened = true;
              break;
            }
          } catch (e: unknown) {
            if (e && typeof e === 'object' && (e as { name?: string }).name === 'AbortError') {
              setEnviando(false);
              return;
            }
          }
          downloadFacturaPdfBlob(pdfBlob, pdfName);
          openFacturaPdfBlobInNewTab(pdfBlob);
          if (!publicPdfUrl) {
            alert(
              `PDF guardado: ${pdfName}\n\n` +
                'No se pudo crear enlace público: en tu proyecto Supabase falta el bucket «empresa-assets» (o no se aplicó la migración). ' +
                'En el dashboard: Storage → crea el bucket o ejecuta las migraciones del repo (supabase/migrations/20260413180000_storage_empresa_assets_bucket.sql). ' +
                'Mientras tanto, adjunta el PDF desde Descargas al chat de WhatsApp.',
            );
          }
          window.open(
            `https://wa.me/${formattedPhone}?text=${encodeURIComponent(messageForWhatsApp)}`,
            '_blank',
            'noopener,noreferrer',
          );
          channelOpened = true;
          break;
        }
        case 'EMAIL': {
          const to = factura.cliente?.email?.trim() || '';
          downloadFacturaPdfBlob(pdfBlob, pdfName);
          openFacturaPdfBlobInNewTab(pdfBlob);
          alert(
            `PDF generado: ${pdfName}\n\n` +
              `Revisa Descargas y la pestaña del visor PDF. En el correo, usa «Adjuntar archivo» y elige ese PDF.\n\n` +
              `(mailto no puede incluir archivos automáticamente.)`,
          );
          const subject = encodeURIComponent(`Factura ${numeroMostrar} - ${currentEmpresa?.nombre || 'Factura'}`);
          const body = encodeURIComponent(
            `${finalMessage}\n\n[Adjunta el archivo ${pdfName} desde tu carpeta de descargas.]`,
          );
          const mailto = `mailto:${to}?subject=${subject}&body=${body}`;
          window.open(mailto, '_blank', 'noopener,noreferrer');
          channelOpened = true;
          break;
        }
        case 'INSTAGRAM': {
          try {
            const shared = await shareFacturaPdfBlob(pdfBlob, pdfName, {
              text: finalMessage,
              title: `Factura ${numeroMostrar}`,
            });
            if (shared) {
              channelOpened = true;
              break;
            }
          } catch (e: unknown) {
            if (e && typeof e === 'object' && (e as { name?: string }).name === 'AbortError') {
              setEnviando(false);
              return;
            }
          }
          downloadFacturaPdfBlob(pdfBlob, pdfName);
          await navigator.clipboard.writeText(finalMessage);
          alert('Mensaje copiado al portapapeles. Se descargó el PDF: adjúntalo en Instagram.');
          channelOpened = true;
          break;
        }
      }

      if (!channelOpened) {
        setEnviando(false);
        return;
      }

      // 2. Log and update DB in background (don't block or fail the send)
      const noteType = canalSeleccionado === 'WHATSAPP' ? 'whatsapp' : canalSeleccionado.toLowerCase();
      supabase
        .from('cliente_notes')
        .insert({
          cliente_id: factura.cliente?.id,
          empresa_id: currentEmpresa.id,
          user_id: usuario.id,
          note_type: noteType,
          content: `Factura ${numeroMostrar} enviada por ${canalSeleccionado}: "${finalMessage.substring(0, 100)}${finalMessage.length > 100 ? '...' : ''}"`,
          metadata: {
            channel: canalSeleccionado.toLowerCase(),
            factura_id: factura.id,
            factura_numero: numeroMostrar,
            total: factura.total,
            message_length: finalMessage.length,
            sent_at: new Date().toISOString(),
            status: 'sent',
          },
        })
        .then(() => {}, (err) => console.warn('EnviarFactura: log note failed', err));

      if (factura.cliente?.id) {
        supabase
          .from('clientes')
          .update({ last_contacted: new Date().toISOString() })
          .eq('id', factura.cliente.id)
          .then(() => {}, (err) => console.warn('EnviarFactura: update last_contacted failed', err));
      }

      // Columna mensajes_enviados no existe en BD (ver useFacturasStore); solo ultima_actividad.
      if (currentEmpresa?.id) {
        supabase
          .from('facturas')
          .update({
            ultima_actividad: new Date().toISOString(),
          })
          .eq('id', factura.id)
          .then(() => {}, (err) => console.warn('EnviarFactura: update factura failed', err));
      }

      if (onEnviar) onEnviar(canalSeleccionado);
      setMensajeEnviado(true);
      setEnviando(false);
      setTimeout(() => onClose(), 2000);
    } catch (error: any) {
      console.error('Error al enviar factura:', error);
      alert('Error al enviar factura. Por favor intenta nuevamente.');
      setEnviando(false);
    }
  };

  const _resetearFormulario = () => {
    setMensajePersonalizado('');
    setMensajeEnviado(false);
  };

  if (mensajeEnviado) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10060] p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            ¡Factura Enviada!
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            La factura {numeroMostrar} ha sido enviada exitosamente por {canalSeleccionado.toLowerCase()}.
          </p>
          <div className="text-xs text-gray-500 mb-4">
            <p>El cliente recibirá una notificación y podrá revisar los detalles.</p>
            <p>Puedes hacer seguimiento del estado en la lista de facturas.</p>
          </div>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Continuar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10060] p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Enviar Factura {numeroMostrar}
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Contenido - scrollable middle */}
        <div className="flex-1 min-h-0 p-6 overflow-y-auto">
          <div className="space-y-6">
            {/* Información de la factura */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Resumen de la Factura</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Cliente:</span>
                  <span className="ml-2 font-medium">{factura.cliente?.nombre || 'Sin cliente'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total:</span>
                  <span className="ml-2 font-medium">${factura.total.toLocaleString('es-CO')}</span>
                </div>
                <div>
                  <span className="text-gray-600">Items:</span>
                  <span className="ml-2 font-medium">{factura.items.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">Vence:</span>
                  <span className="ml-2 font-medium">{factura.fechaEntrega.toLocaleDateString('es-ES')}</span>
                </div>
              </div>
            </div>

            {/* Selección de canal */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Canal de Envío</h4>
              <div className="grid grid-cols-3 gap-3">
                {(['WHATSAPP', 'EMAIL', 'INSTAGRAM'] as const).map((canal) => (
                  <button
                    key={canal}
                    onClick={() => {
                      setCanalSeleccionado(canal);
                      setMensajePersonalizado(mensajesPredeterminados[canal]);
                    }}
                    className={`p-4 border-2 rounded-lg text-center transition-colors ${
                      canalSeleccionado === canal
                        ? obtenerColorCanal(canal)
                        : 'border-gray-200 hover:border-gray-300'
                    } ${obtenerColorCanalHover(canal)}`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      {obtenerIconoCanal(canal)}
                      <span className="text-sm font-medium">
                        {canal === 'WHATSAPP' && 'WhatsApp'}
                        {canal === 'EMAIL' && 'Email'}
                        {canal === 'INSTAGRAM' && 'Instagram'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Información del cliente para el canal seleccionado */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-md font-medium text-blue-900 mb-2">Información de Contacto</h4>
              <div className="text-sm text-blue-800">
                {canalSeleccionado === 'WHATSAPP' && (
                  <p>📱 <strong>WhatsApp:</strong> {factura.cliente?.telefono || 'No disponible'}</p>
                )}
                {canalSeleccionado === 'EMAIL' && (
                  <p>📧 <strong>Email:</strong> {factura.cliente?.email || 'No disponible'}</p>
                )}
                {canalSeleccionado === 'INSTAGRAM' && (
                  <p>📸 <strong>Instagram:</strong> Buscar por nombre: {factura.cliente?.nombre || 'Cliente'}</p>
                )}
              </div>
            </div>

            {/* Mensaje personalizado */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Mensaje Personalizado</h4>
              <textarea
                value={mensajePersonalizado}
                onChange={(e) => setMensajePersonalizado(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Personaliza el mensaje que se enviará con la factura..."
              />
              <div className="mt-2 flex flex-wrap justify-between items-center gap-2">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setMensajePersonalizado(mensajesPredeterminados[canalSeleccionado])}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Restaurar mensaje predeterminado
                  </button>
                  <button
                    type="button"
                    onClick={copiarMensaje}
                    className="text-sm text-gray-600 hover:text-gray-800 border border-gray-300 px-2 py-1 rounded"
                  >
                    {copiado ? '✓ Copiado' : 'Copiar mensaje'}
                  </button>
                </div>
                <span className="text-xs text-gray-500">
                  {mensajePersonalizado.length} caracteres
                </span>
              </div>
            </div>

            {/* Métodos de Pago */}
            {availablePaymentMethods.length > 0 && (
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  Métodos de Pago a Incluir
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-600 mb-3">
                    Selecciona los métodos de pago que quieres incluir en el mensaje:
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {availablePaymentMethods.map((method) => (
                      <label
                        key={method.id}
                        className="flex items-start gap-3 p-2 hover:bg-white rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPaymentMethods.includes(method.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPaymentMethods([...selectedPaymentMethods, method.id]);
                            } else {
                              setSelectedPaymentMethods(
                                selectedPaymentMethods.filter(id => id !== method.id)
                              );
                            }
                          }}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{method.icon || '💳'}</span>
                            <span className="font-medium text-sm text-gray-900">
                              {method.method_name}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {method.method_type === 'transferencia' && 
                              `${method.account_info.banco || ''} - ${method.account_info.numero_cuenta || ''}`
                            }
                            {method.method_type === 'nequi' && 
                              `Teléfono: ${method.account_info.telefono || ''}`
                            }
                            {method.method_type === 'link_pago' && 
                              `Link de pago`
                            }
                            {!['transferencia', 'nequi', 'link_pago'].includes(method.method_type) && 
                              method.description
                            }
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                  {selectedPaymentMethods.length > 0 && (
                    <p className="text-xs text-blue-600 mt-3">
                      ✓ {selectedPaymentMethods.length} método(s) seleccionado(s) - Se agregarán al final del mensaje
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Channel-specific send instructions (no WhatsApp message when Email is selected) */}
            {canalSeleccionado === 'WHATSAPP' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-semibold text-amber-900">Envío por WhatsApp</h4>
                <p className="text-sm text-amber-800">
                  Se genera el PDF y, si se puede, se sube a Storage y se añade un <strong>enlace público al PDF</strong> al
                  mensaje (el cliente puede abrirlo sin adjuntar). En móvil también se intenta compartir el archivo; en
                  escritorio conserva la descarga local como respaldo.
                </p>
              </div>
            )}
            {canalSeleccionado === 'EMAIL' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-semibold text-blue-900">Envío por correo</h4>
                <p className="text-sm text-blue-800">
                  Al enviar se genera el <strong>PDF real</strong>, se descarga y se abre en una pestaña; luego se abre tu
                  correo con el mensaje. Debes <strong>adjuntar manualmente</strong> el PDF (ningún navegador adjunta
                  archivos por mailto).
                </p>
              </div>
            )}
            {canalSeleccionado === 'INSTAGRAM' && (
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-semibold text-pink-900">Envío por Instagram</h4>
                <p className="text-sm text-pink-800">
                  Al enviar se copiará el mensaje. Pégalo en el DM de Instagram y adjunta el PDF si lo descargaste.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer - always visible; Enviar Factura is the main action */}
        <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              <button
                type="button"
                onClick={() => void descargarPDF()}
                disabled={pdfBusy || enviando}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                title="Descargar factura en PDF"
              >
                <DocumentArrowDownIcon className="w-5 h-5" />
                {pdfBusy ? 'Generando PDF…' : 'Descargar PDF'}
              </button>
              <button
                type="button"
                onClick={() => void compartirPDF()}
                disabled={pdfBusy || enviando}
                className={`px-4 py-2 border rounded-md transition-colors flex items-center gap-2 disabled:opacity-50 ${
                  shareAvailable
                    ? 'border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100'
                    : 'border-gray-200 text-gray-400 bg-gray-50'
                }`}
                title={
                  shareAvailable
                    ? 'Compartir PDF (AirDrop, Mail, Mensajes…)'
                    : 'Compartir no disponible en este navegador (usa Safari en Mac/iPhone)'
                }
              >
                <Share2 className="w-5 h-5" aria-hidden />
                Compartir PDF
              </button>
              <div className="text-sm text-gray-600">
                <span className="font-medium">Canal:</span>{' '}
                <span className="capitalize">{canalSeleccionado.toLowerCase()}</span>
                {!canSendByChannel && (
                  <span className="block text-amber-600 mt-1">
                    {canalSeleccionado === 'EMAIL'
                      ? 'El cliente no tiene email. Edita el cliente para agregar uno.'
                      : 'El cliente no tiene teléfono. Edita el cliente para agregar uno.'}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={enviarFactura}
                disabled={sendDisabled}
                className={`px-8 py-3 rounded-lg font-semibold text-white flex items-center space-x-2 shadow-lg transition-all ${
                  sendDisabled
                    ? 'bg-gray-400 cursor-not-allowed'
                    : canalSeleccionado === 'WHATSAPP'
                    ? 'bg-green-600 hover:bg-green-700 hover:shadow-xl'
                    : canalSeleccionado === 'EMAIL'
                    ? 'bg-blue-600 hover:bg-blue-700 hover:shadow-xl'
                    : 'bg-pink-600 hover:bg-pink-700 hover:shadow-xl'
                }`}
              >
                {enviando ? (
                  <>
                    <ClockIcon className="w-5 h-5 animate-spin" />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="w-5 h-5" />
                    <span className="text-base">Enviar Factura</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
